import React from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import './sidebar.scss';

// Icons as simple components
const StatsIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M18 20V10M12 20V4M6 20v-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const ChatIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const KnowledgeIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20M4 19.5A2.5 2.5 0 0 0 6.5 22H20V2H6.5A2.5 2.5 0 0 0 4 4.5v15z" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const SettingsIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
);



const LinkIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const navItems = [
    { id: 'overview', label: 'Overview', icon: StatsIcon, route: 'dashboard/overview' },
    {
        id: 'dashboard', label: 'My Chatbots', icon: () => (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="7" height="7" />
                <rect x="14" y="3" width="7" height="7" />
                <rect x="14" y="14" width="7" height="7" />
                <rect x="3" y="14" width="7" height="7" />
            </svg>
        ), route: 'dashboard'
    },
    { id: 'knowledge-sources', label: 'Knowledge Base', icon: KnowledgeIcon, route: 'dashboard/knowledge' }, // Global
    { id: 'stats', label: 'Overview', icon: StatsIcon, route: 'stats' }, // Project Homepage
    { id: 'chat-history', label: 'History', icon: ChatIcon, route: 'chat-history' }, // Project
    { id: 'chatbot-settings', label: 'Settings', icon: SettingsIcon, route: 'settings' }, // Project
    { id: 'integration', label: 'Integration', icon: LinkIcon, route: 'integration' }, // Project
];

export default function Sidebar() {
    const { route, projectId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    // Check Global Context
    const isDashboard = location.pathname.includes('/dashboard') || location.pathname === '/' || location.pathname.includes('/settings/');
    // const isGlobalKnowledge = location.pathname.includes('/global-knowledge');

    const handleNavigate = (routePath) => {
        if (routePath.includes('dashboard')) {
            // Global Route
            navigate(`/${routePath}`);
        } else if (projectId) {
            // Project Route
            if (routePath === 'stats') {
                // Deprecated or redirect to settings?
                navigate(`/project/${projectId}`);
            } else {
                navigate(`/project/${projectId}/${routePath}`);
            }
        }
    };

    const isActive = (itemRoute) => {
        if (isDashboard) {
            // Global Context
            const currentPath = location.pathname;

            // Special case for 'Overview' (default /)
            if (currentPath === '/' && itemRoute === 'dashboard/overview') return true;

            // Special case for 'My Chatbots' (exact match only to avoid matching /dashboard/overview)
            if (itemRoute === 'dashboard') {
                return currentPath === '/dashboard';
            }

            return currentPath.includes(itemRoute);
        }

        if (projectId) {
            // Project Context
            // Settings can be /settings or /chatbot-settings
            if (itemRoute === 'settings' && (route === 'settings' || route === 'chatbot-settings')) return true;

            // Overview (stats) is the default when route is undefined
            if (itemRoute === 'stats' && route === undefined) return true;

            return route === itemRoute;
        }
        return false;
    };

    // Filter items based on context
    const visibleNavItems = navItems.filter(item => {
        // If in settings (e.g. User Management), hide standard nav items, only "Back" button will be shown
        if (location.pathname.includes('/settings/')) {
            return false;
        }

        if (isDashboard) {
            // Show Global Items (include settings if user is admin - logic handled in route rendering but link exists)
            // Ideally we check if user is Admin to show User Management link, but for now just showing global items
            return ['overview', 'dashboard', 'knowledge-sources'].includes(item.id);
        }
        if (projectId) {
            // Show Project Items
            return ['stats', 'chat-history', 'chatbot-settings', 'integration'].includes(item.id);
        }
        return false; // Should not happen
    });

    return (
        <aside className="sidebar">
            <nav className="sidebar-nav">
                {(projectId || location.pathname.includes('/settings/')) && (
                    <button
                        className="sidebar-item back-btn"
                        onClick={() => navigate('/dashboard/overview')}
                        style={{ marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid rgba(255,255,255,0.05)' }}
                    >
                        <span className="sidebar-icon">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </span>
                        <span className="sidebar-label">Back to Dashboard</span>
                    </button>
                )}
                {visibleNavItems.map((item) => (
                    <button
                        key={item.id}
                        className={`sidebar-item ${isActive(item.route) ? 'active' : ''}`}
                        onClick={() => handleNavigate(item.route)}
                    >
                        <span className="sidebar-icon">
                            <item.icon />
                        </span>
                        <span className="sidebar-label">{item.label}</span>
                    </button>
                ))}
            </nav>

            <div className="sidebar-footer">
                <div className="sidebar-version">v1.0.0</div>
            </div>
        </aside>
    );
}
